/** @jsx isolateComponent */

import type { MouseEvent as ReactMouseEvent } from 'react';
import { isolateComponent } from '@zapier/style-encapsulation';
/* eslint-disable import/no-duplicates */
import { Colors, IconButton } from '@zapier/design-system';
import type { IconName } from '@zapier/design-system';

type Props = {
  /**
   * ARIA attribute indicating whether or not the content
   * controlled by the button is visible.
   */
  ariaExpanded: boolean;
  /**
   * ARIA attribute to label the button if `label` is empty.
   */
  ariaLabel?: string;
  /**
   * Name of the `Icon` to render.
   */
  icon: IconName;
  /**
   *  Indicates what the button does. Used for the `ariaLabel` on the
   * `Button` as well as the text for the `Tooltip`.
   * This prop should be supplied in most circumstances, but in cases
   * **when it's not desired be sure to pass `ariaLabel`!**
   */
  label: string;
  /** Callback to be executed when the button is clicked. */
  onClick?: (e: ReactMouseEvent) => void;
};

const MenuCollapseButtonTheme = {
  custom: {
    base: {
      backgroundColor: 'transparent',
      color: Colors.grey700,
    },
    disabled: {
      backgroundColor: 'transparent',
      color: Colors.grey100,
    },
    hover: {
      backgroundColor: Colors.grey100,
      color: Colors.grey700,
    },
    focus: {
      backgroundColor: Colors.grey100,
      color: Colors.grey700,
      outlineColor: Colors.transparent,
    },
  },
};
/**
 * @private
 * MenuCollapseButton renders a themed IconButton.
 */
const MenuCollapseButton = (props: Props) => (
  <IconButton
    ariaExpanded={props.ariaExpanded}
    ariaLabel={props.ariaLabel}
    color="custom"
    colorTheme={MenuCollapseButtonTheme}
    icon={props.icon}
    iconSize={30}
    isPill={true}
    label={props.label}
    onClick={(event: ReactMouseEvent) => {
      if (props.onClick) {
        props.onClick(event);
      }
    }}
    size="medium"
    tooltipPosition="east"
  />
);

MenuCollapseButton.defaultProps = {
  ariaExpanded: false,
  icon: 'navMenu',
  label: 'Menu',
};

export default MenuCollapseButton;
